<template>
  <div>
    <!-- Loading skeleton component -->
    <CoachSectionSkeleton v-if="isLoading" />

    <div
      v-else-if="!hasWeaknesses"
      class="flex grid lg:grid-cols-12 w-full gap-6"
    >
      <div class="col-span-12 sm:col-span-9 flex flex-col gap-4">
        <div class="flex items-center gap-2">
          <h1 class="text-text-primary font-semibold text-[24px]">{{ $t('home.coach.title') }}</h1>
          <div
            class="text-[10px] rounded-md px-1 py-[1px] mr-1 self-start"
            style="
              background-color: rgba(208, 113, 197, 0.125);
              color: rgb(208, 113, 197);
            "
          >
            {{ $t('home.coach.beta') }}
          </div>
        </div>
        <div
          class="flex-1 flex flex-col gap-2 w-full bg-gray-900 rounded-lg p-10 items-center justify-center relative"
        >
          <div class="absolute top-0 left-0 w-full h-full topography-bg" />
          <div
            v-if="!showMatches || userStatsError?.data?.message == 'Not Found'"
            class="flex flex-col gap-4 text-center items-center z-50"
          >
            <div
              class="min-w-[48px] w-[48px] h-[48px] rounded-full flex justify-center items-center button-bg"
            >
              <img class="w-6 h-6" src="../../assets/icons/stats-icon.svg" />
            </div>
            <h1 class="text-text-primary font-medium text-lg">
              {{ $t('home.coach.disabled_title') }}
            </h1>
            <i18n-t scope="global" keypath="home.coach.disabled_body" tag="h2" class="text-text-secondary text-sm leading-5">
              <template #br><br /></template>
              <template #discord>
                <a
                  href="https://discord.gg/refrag"
                  target="_blank"
                  class="underline"
                >Discord</a
                >
              </template>
            </i18n-t>
          </div>
          <div
            v-else-if="user.access_level < 1"
            class="flex flex-col gap-4 text-center items-center z-50"
          >
            <div
              class="min-w-[48px] w-[48px] h-[48px] rounded-full flex justify-center items-center button-bg"
            >
              <img class="w-6 h-6" src="../../assets/icons/stats-icon.svg" />
            </div>
            <h1 class="text-text-primary font-medium text-lg">
              {{ $t('home.coach.upsell_title') }}
            </h1>
            <i18n-t scope="global" keypath="home.coach.upsell_body" tag="h2" class="text-text-secondary text-sm leading-5">
              <template #br><br /></template>
            </i18n-t>
            <router-link
              to="/upgrade"
              class="py-3 w-2/3 rounded-lg text-text-primary text-xs font-medium duration-300 bg-primary-600 hover:bg-primary-500 flex items-center justify-center gap-2"
            >
              {{ $t('common.upgrade') }}
              <ArrowRightIcon class="w-4 h-4 text-text-primary" />
            </router-link>
          </div>
          <div
            v-else-if="noRankComparison"
            class="flex flex-col gap-4 text-center items-center z-50"
          >
            <h1 class="text-text-primary font-medium text-lg">
              {{ $t('home.coach.no_rank_comparison_title') }}
            </h1>
            <i18n-t scope="global" keypath="home.coach.no_rank_comparison_body_wrapped" tag="h2" class="text-text-secondary text-sm leading-5">
              <template #br><br /></template>
            </i18n-t>
          </div>
          <div
            v-else-if="
              (props.userSettings?.has_valve_matches && props.userSettings?.match_auth_token)
                || props.userSettings?.faceit_player_id
            "
            class="flex flex-col gap-4 text-center items-center z-50"
          >
            <h1 class="text-text-primary font-medium text-lg">{{ $t('home.coach.play_more_title') }}</h1>
            <i18n-t scope="global" keypath="home.coach.play_more_body" tag="h2" class="text-text-secondary text-sm leading-5">
              <template #br><br /></template>
              <template #link>
                <a
                  href="https://discord.com/invite/Refrag"
                  target="_blank"
                  class="underline"
                >{{ $t('home.coach.contact_support') }}</a
                >
              </template>
            </i18n-t>
          </div>
          <div
            v-else-if="
              (!props.userSettings?.has_valve_matches || !props.userSettings?.match_auth_token)
                && !props.userSettings?.faceit_player_id
            "
            class="flex flex-col gap-4 text-center items-center z-50"
          >
            <h1 class="text-text-primary font-medium text-lg">{{ $t('home.coach.update_tokens_title') }}</h1>
            <i18n-t scope="global" keypath="home.coach.update_tokens_body" tag="h2" class="text-text-secondary text-sm leading-5">
              <template #br><br /></template>
            </i18n-t>
            <router-link
              to="/settings"
              class="py-3 w-2/3 rounded-lg text-text-primary text-xs font-medium duration-300 bg-primary-600 hover:bg-primary-500 flex items-center justify-center gap-2"
            >
              {{ $t('home.coach.go_to_settings') }}
              <ArrowRightIcon class="w-4 h-4 text-text-primary" />
            </router-link>
          </div>
        </div>
      </div>

      <div
        v-if="!showMatches || userStatsError?.data?.message == 'Not Found'"
        class="flex flex-col gap-4 col-span-12 sm:col-span-3 sm:justify-start"
      >
        <h1 class="h-[36px] text-text-primary font-semibold text-[24px]">{{ $t('home.coach.routine_title') }}</h1>
        <div class="flex-1 bg-gray-900 rounded-lg p-4 flex flex-col gap-2">
          <div
            class="w-16 h-16 bg-gray-800 border border-gray-700 rounded-full flex items-center justify-center mb-2"
          >
            <img src="../../assets/icons/routines.svg" class="w-8 h-8" />
          </div>

          <h1 class="text-text-primary">{{ $t('home.coach.disabled_title') }}</h1>

          <i18n-t scope="global" keypath="home.coach.disabled_body" tag="h2" class="text-text-secondary text-xs font-light flex-1">
            <template #br><br /></template>
            <template #discord>
              <a
                href="https://discord.gg/refrag"
                target="_blank"
                class="underline"
              >Discord</a
              >
            </template>
          </i18n-t>
        </div>
      </div>

      <div
        v-else-if="user.access_level < 1"
        class="flex flex-col gap-4 col-span-12 sm:col-span-3 sm:justify-start"
      >
        <h1 class="text-text-primary font-semibold text-[24px]">{{ $t('home.coach.routine_title') }}</h1>
        <div class="flex-1 bg-gray-900 rounded-lg p-4 flex flex-col gap-2">
          <div
            class="w-16 h-16 bg-gray-800 border border-gray-700 rounded-full flex items-center justify-center mb-2"
          >
            <img src="../../assets/logos/refrag-icon.svg" class="w-8 h-8" />
          </div>

          <h1 class="text-text-primary">{{ $t('home.coach.upgrade_routine_title') }}</h1>

          <h2 class="text-text-secondary text-xs font-light flex-1">
            {{ $t('home.coach.upgrade_routine_body') }}
          </h2>

          <router-link
            to="/upgrade"
            class="mt-6 py-3 w-full rounded-lg text-text-primary text-xs font-medium duration-300 bg-gray-800 hover:bg-gray-700 flex items-center justify-center gap-2"
          >
            {{ $t('home.coach.upgrade_now') }}
          </router-link>
        </div>
      </div>

      <!-- A routine outlives the weaknesses that seeded it, so it keeps its slot
           whatever the left card ended up saying. -->
      <div
        v-else-if="dailyRoutines.length > 0 && selectedRoutine"
        class="flex flex-col gap-4 col-span-12 sm:col-span-3 sm:justify-start h-full"
      >
        <h1 class="text-text-primary font-semibold text-[24px]">{{ $t('home.coach.routines_title') }}</h1>
        <Routine
          :routines="dailyRoutines"
          :user="user"
          @launchRoutine="launchRoutine"
          @refreshRoutines="refreshRoutines"
          class="col-span-12 sm:col-span-3 flex-1"
        />
      </div>

      <!-- Mirrors the left card: this column has its own chain, and without this
           arm "No rank comparison available" sat next to "Add Tokens to get
           started". -->
      <div
        v-else-if="noRankComparison"
        class="flex flex-col gap-4 col-span-12 sm:col-span-3 sm:justify-start"
      >
        <h1 class="text-text-primary font-semibold text-[24px]">{{ $t('common.recommended') }}</h1>
        <div class="flex-1 bg-gray-900 rounded-lg p-4 flex flex-col gap-2">
          <div
            class="w-16 h-16 bg-gray-800 border border-gray-700 rounded-full flex items-center justify-center mb-2"
          >
            <img src="../../assets/logos/refrag-icon.svg" class="w-8 h-8" />
          </div>

          <h1 class="text-text-primary">{{ $t('home.coach.no_rank_comparison_title') }}</h1>

          <h2 class="text-text-secondary text-xs font-light flex-1">
            {{ $t('home.coach.no_rank_comparison_body') }}
          </h2>
        </div>
      </div>

      <div
        v-else-if="
          props.userSettings?.has_valve_matches
            && props.userSettings?.faceit_player_id
            && props.userSettings?.match_auth_token
        "
        class="flex flex-col gap-4 col-span-12 sm:col-span-3 sm:justify-start"
      >
        <h1 class="text-text-primary font-semibold text-[24px]">{{ $t('common.recommended') }}</h1>
        <div class="flex-1 bg-gray-900 rounded-lg p-4 flex flex-col gap-2">
          <div
            class="w-16 h-16 bg-gray-800 border border-gray-700 rounded-full flex items-center justify-center mb-2"
          >
            <img src="../../assets/logos/refrag-icon.svg" class="w-8 h-8" />
          </div>

          <h1 class="text-text-primary">{{ $t('home.coach.play_more_title') }}</h1>

          <h2 class="text-text-secondary text-xs font-light flex-1">
            {{ $t('home.coach.play_more_short_body') }}
          </h2>
        </div>
      </div>

      <div v-else class="flex flex-col gap-4 col-span-12 sm:col-span-3 sm:justify-start">
        <h1 class="text-text-primary font-semibold text-[24px]">{{ $t('common.recommended') }}</h1>
        <div class="flex-1 bg-gray-900 rounded-lg p-4 flex flex-col gap-2">
          <div
            class="w-16 h-16 bg-gray-800 border border-gray-700 rounded-full flex items-center justify-center mb-2"
          >
            <img src="../../assets/logos/refrag-icon.svg" class="w-8 h-8" />
          </div>

          <h1 class="text-text-primary">{{ $t('home.coach.add_tokens_title') }}</h1>

          <h2 class="text-text-secondary text-xs font-light flex-1">
            {{ $t('home.coach.add_tokens_body') }}
          </h2>

          <router-link
            to="/settings"
            class="mt-6 py-3 w-full rounded-lg text-text-primary text-xs font-medium duration-300 bg-gray-800 hover:bg-gray-700 flex items-center justify-center gap-2"
          >
            {{ $t('home.coach.go_to_settings') }}
          </router-link>
        </div>
      </div>
    </div>
    <div v-else class="flex grid lg:grid-cols-12 w-full gap-6">
      <div class="col-span-12 sm:col-span-9 flex flex-col gap-4">
        <div class="flex items-center gap-2">
          <h1 class="text-text-primary font-semibold text-[24px]">{{ $t('home.coach.title') }}</h1>
          <div
            class="text-[10px] rounded-md px-1 py-[1px] mr-1 self-start"
            style="
              background-color: rgba(208, 113, 197, 0.125);
              color: rgb(208, 113, 197);
            "
          >
            {{ $t('home.coach.beta') }}
          </div>
          <div class="has-tooltip">
            <img
              src="../../assets/icons/info-icon.svg"
              :alt="$t('home.coach.info_icon_alt')"
              class="w-6 h-6 cursor-pointer"
            />
            <div class="tooltip bg-gray-900 rounded-lg p-2 ml-8 -mt-7">
              <p class="text-text-secondary text-xs">
                {{ $t('home.coach.rank_comparison_tooltip') }}
              </p>
            </div>
          </div>
        </div>
        <div class="bg-gray-900 rounded-lg flex flex-col flex-grow">
          <nav class="w-full grid grid-cols-3 items-center justify-center relative">
            <div
              v-for="(stat, index) in userStats.slice(0, 3)"
              :key="index"
              class="py-3 flex flex-col gap-2 col-span-1 items-center justify-center cursor-pointer relative border-b border-gray-800"
              @click="selectStat(stat)"
            >
              <h2 class="text-text-secondary text-xs">
                {{ toTitleCase(formatStatString(stat.data.name)) }}
              </h2>
            </div>
            <div class="sliding-border" :style="{ left: slidingBorderPosition }" />
          </nav>

          <div class="flex p-4 gap-4 sm:gap-16 flex-grow flex-col sm:flex-row">
            <div class="flex-1 flex flex-col gap-3">
              <h1 class="text-text-primary font-medium text-lg">
                {{ toTitleCase(formatStatString(selectedStat?.data?.name)) }}
              </h1>

              <i18n-t
                scope="global"
                keypath="home.coach.stat_comparison"
                tag="div"
                class="text-text-secondary text-sm xl:w-3/4"
              >
                <template #stat>{{ formatStatString(selectedStat?.data?.name) }}</template>
                <template #delta>
                  <span
                    :class="
                      selectedStat?.data?.is_negative
                        ? 'text-danger-400'
                        : 'text-success-400'
                    "
                  >
                    {{
                      $t(selectedStat?.data.is_negative ? "home.coach.worse_by" : "home.coach.better_by", {
                        percent: Math.abs(
                          parseFloat(
                            parseFloat(selectedStat?.data.percent_difference).toFixed(2),
                          ),
                        ),
                      })
                    }}
                  </span>
                </template>
              </i18n-t>

              <div class="w-full h-px bg-gray-800 xl:w-4/5" />

              <div class="text-text-secondary text-sm xl:w-3/4">
                {{
                  $t(
                    selectedStat?.data?.is_negative
                      ? "home.coach.improve_prompt"
                      : "home.coach.maintain_prompt",
                    { stat: formatStatString(selectedStat?.data?.name) },
                  )
                }}
              </div>

              <button
                @click="
                  launchRecommendedMod(selectedStatMod?.modifier?.modifier_name, true)
                "
                class="mt-auto py-3 w-full rounded-lg text-text-primary text-xs font-medium duration-300 bg-primary-600 hover:bg-primary-500 flex items-center justify-center gap-2"
              >
                {{ selectedStat?.data.is_negative ? $t("home.coach.improve_now") : $t("home.coach.practice_now") }}
                <svg
                  v-if="launchLoading === 'prepare_set'"
                  width="16"
                  height="16"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="animate-spin"
                >
                  <circle cx="16" cy="16" r="15" stroke="#1E2733" stroke-width="2" />
                  <path
                    d="M16 1C17.9698 1 19.9204 1.38799 21.7403 2.14181C23.5601 2.89563 25.2137 4.00052 26.6066 5.3934C27.9995 6.78628 29.1044 8.43986 29.8582 10.2597C30.612 12.0796 31 14.0302 31 16"
                    stroke="#4E54C8"
                    stroke-width="2"
                  />
                </svg>
                <HandThumbUpIcon
                  v-if="launchSuccess === 'prepare_set'"
                  class="w-4 h-4 text-success-400"
                />
                <ArrowRightIcon class="w-4 h-4 text-text-primary" />
              </button>
            </div>

            <div class="flex-1 flex items-stretch p-2">
              <div
                class="flex-1 bg-gray-800 rounded-lg border-gray-700 border p-5 flex flex-col"
              >
                <div class="flex-1 flex gap-8">
                  <div class="flex flex-col gap-1">
                    <div class="flex gap-1 items-center">
                      <div class="h-2 w-2 rounded-sm bg-primary-500" />
                      <h2 class="text-text-secondary text-xs">{{ $t('home.coach.your_avg') }}</h2>
                    </div>
                    <h1 class="text-text-primary text-lg font-medium">
                      {{
                        formatStatStringData(
                          selectedStat?.data?.players_value,
                          selectedStat?.data?.unit_type,
                        )
                      }}
                    </h1>
                  </div>
                  <div class="flex flex-col gap-1">
                    <div class="flex gap-1 items-center">
                      <div class="h-2 w-2 rounded-sm bg-secondary-500" />
                      <h2 class="text-text-secondary text-xs">{{ $t('home.coach.target_avg') }}</h2>
                    </div>
                    <h1 class="text-text-primary text-lg font-medium">
                      {{
                        formatStatStringData(
                          selectedStat?.data?.rating_range_average_value,
                          selectedStat?.data?.unit_type,
                        )
                      }}
                    </h1>
                  </div>
                </div>
                <LineChart
                  v-if="chartData"
                  :options="options"
                  :chartData="chartData"
                  class="h-[112px]"
                />
                <div v-else class="h-[112px] flex items-center justify-center">
                  {{ $t('home.coach.loading_chart') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="flex flex-col gap-4 col-span-12 sm:col-span-3 sm:justify-start h-full"
        v-if="dailyRoutines.length > 0 && selectedRoutine"
      >
        <h1 class="text-text-primary font-semibold text-[24px]">{{ $t('home.coach.routines_title') }}</h1>
        <Routine
          :routines="dailyRoutines"
          :user="user"
          @launchRoutine="launchRoutine"
          @refreshRoutines="refreshRoutines"
          class="col-span-12 sm:col-span-3 flex-1"
        />
      </div>

      <div v-else class="flex flex-col gap-4 col-span-12 sm:col-span-3 sm:justify-start h-full">
        <h1 class="text-text-primary font-semibold text-[24px]">{{ $t('common.recommended') }}</h1>
        <div class="bg-gray-900 rounded-lg p-4 flex flex-col gap-2 flex-1">
          <div
            class="w-16 h-16 bg-gray-800 border border-gray-700 rounded-full flex items-center justify-center mb-2"
          >
            <img :src="`/maps/map_icon_${recommendedMap?.name}.svg`" class="w-8 h-8" />
          </div>
          <div class="flex gap-1 items-center">
            <h2 class="text-text-secondary text-[10px] uppercase tracking-[1.5px]">
              {{ recommendedMap?.friendly_name }}
            </h2>
            <span class="text-text-disabled text-[12px] uppercase tracking-[1.5px]"
            >•</span
            >
            <h2 class="text-text-secondary text-[10px] uppercase tracking-[1.5px]">
              {{ recommendedMod?.modifier?.friendly_name }}
            </h2>
          </div>

          <h1 class="text-text-primary">{{ $t('home.coach.recommended_mod_title') }}</h1>

          <h2 class="text-text-secondary text-xs font-light flex-1">
            {{
              $t('home.coach.recommended_mod_body', {
                mod: recommendedMod?.modifier?.friendly_name,
                stat: formatStatString(recommendedStat?.data?.name),
                map: recommendedMap?.friendly_name,
              })
            }}
          </h2>

          <!-- With no mod name there is nothing to launch, and the click is a
               no-op — don't offer the button at all. -->
          <button
            v-if="recommendedMod?.modifier?.modifier_name"
            @click="launchRecommendedMod(recommendedMod.modifier.modifier_name, false)"
            class="mt-auto py-3 w-full rounded-lg text-text-primary text-xs font-medium duration-300 bg-gray-800 hover:bg-gray-700 flex items-center justify-center gap-2"
          >
            {{ $t('home.coach.play_now') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref, watch, computed, onMounted, onUnmounted,
} from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import axios from 'axios';
import {
  HandThumbUpIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/vue/20/solid';
import { Chart, registerables } from 'chart.js';
import { DoughnutChart, LineChart } from 'vue-chart-3';
import {
  formatStatString,
  toTitleCase,
  formatData,
  formatStatStringData,
} from '../../utils/formatters';
import { createClickablePointsPlugin } from '../../utils/chartPlugins';
import Routine from '../Coach/Routine.vue';
import { lengthToTimeMap } from '../../utils/coach_routines';
import CoachSectionSkeleton from './CoachSectionSkeleton.vue';
import { fetchCoachProfile, timeToKillSeconds } from '../../utils/coach_pipeline';
import {
  findWorstDeviationsAway,
  rankComparisonUnavailable,
} from '../../utils/coach_weaknesses.js';

const router = useRouter();
const store = useStore();

const emit = defineEmits(['launchRecommendedMod', 'loadingStateChanged', 'startServer']);

const props = defineProps({
  user: Object,
  showMatches: Boolean,
  userStatsHistory: Array,
  userSettings: Object,
});

// Move refs from HomePage
const userStats = ref();
const userStatsError = ref(false);
const allStats = ref({});
const selectedStat = ref(null);
const selectedStatMod = ref(null);
const recommendedMap = ref(null);
const mostPlayedMaps = ref([]);
const recommendedMod = ref(null);
const activeServer = computed(() => store.getters.activeServer);
const selectedLength = ref('Short');
const dailyRoutines = ref([]);
const matchCount = ref(null);
const launchLoading = ref(false);
const launchSuccess = ref(false);
const isLoading = ref(true);
const recommendedStat = computed(() => userStats.value?.[0]);
const serverInfo = ref(null);

const selectedRoutine = computed(() => dailyRoutines.value?.find((routine) => routine.duration_in_minutes === lengthToTimeMap[selectedLength.value]));

// Create the plugin. The history is the parent's — chart.js reverses it for
// display, so the click index counts back from the end.
const clickablePointsPlugin = createClickablePointsPlugin(
  (index) => {
    const history = props.userStatsHistory || [];

    return history[history.length - 1 - index]?.match_id;
  },
  (matchId) => router.push(`/matches/${matchId}`),
);

// Register the plugin
Chart.register(...registerables, clickablePointsPlugin);

// An empty weakness list is a real state (no rank average for any stat), and []
// is truthy, so the populated branch has to be gated on a stat to render.
const hasWeaknesses = computed(() => !!userStats.value?.length && !!selectedStat.value);

// Nothing played yet. Distinct from "no bracket data": both leave every rank
// average null, but only this one is the player's to fix by playing.
const hasNoMatches = computed(() => matchCount.value === 0);

// Tells "we have no bracket to compare you against" apart from "play more
// matches": ProfileBuilder nulls the rank average for every stat in that case.
// Gated on having matches, or it wins the chain for a brand new player and
// buries the message that actually applies to them.
const noRankComparison = computed(
  () => !hasNoMatches.value && rankComparisonUnavailable(allStats.value),
);

// Add computed properties
const slidingBorderPosition = computed(() => {
  const index = userStats.value?.findIndex(
    (stat) => stat.title === selectedStat.value?.title,
  );
  return `${index * 33.33}%`;
});

// Add functions
function selectStat(stat) {
  selectedStat.value = stat;
  updateSelectedStatMod();
}

function getDailyRoutines() {
  axios({
    url: '/task_lists/my_daily_routine',
    method: 'GET',
  })
    .then((res) => {
      dailyRoutines.value = res.data;
    })
    .catch((err) => {
      console.log(err);
    });
}

// Initialize routine data but don't affect main loading state
getDailyRoutines();

function refreshRoutines() {
  // Re-fetch the daily routines after refresh
  getDailyRoutines();
}

function launchRoutine(routine) {
  // Always open StartServerModal so the map/preset override panel shows, whether
  // the server is already live or still needs to be started. This mirrors the
  // individual routine page (RoutinePage.vue) behavior.
  emit('startServer', {
    mode: 'Creator Studio',
    content: {
      task_list_id: routine.id,
      is_exam: false,
      title: routine.name,
      type: 'task',
      use_difficulty: 1, // Jimi requested we set this to 1 by default
    },
  });
}

function launchRecommendedMod(mod, is_practice) {
  // Without a mod name StartServerModal opens on `{ mode: undefined }`, which
  // renders an empty modal the user cannot do anything with.
  if (!mod) {
    return;
  }

  emit(
    'launchRecommendedMod',
    mod,
    is_practice,
    timeToKillSeconds(allStats.value),
    recommendedMap.value,
  );
}

function updateSelectedStatMod() {
  selectedStatMod.value = getMod(selectedStat.value?.data?.recommended_mods);
}

function getMod(mods) {
  if (!mods || mods.length === 0) {
    return null;
  }

  // Calculate total weight of all mods
  const totalWeight = mods.reduce((sum, mod) => sum + mod.value, 0);

  // Generate random value between 0 and total weight
  const random = Math.random() * totalWeight;

  // Find the mod where random falls within its weight range
  let weightSum = 0;
  for (const mod of mods) {
    weightSum += mod.value;
    if (random <= weightSum) {
      return mod;
    }
  }

  // Fallback to first mod if something goes wrong
  return mods[0];
}

// Chart options
const options = computed(() => ({
  spanGaps: true,
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
    clickablePoints: clickablePointsPlugin,
  },
  elements: {
    point: {
      radius: 4,
      hoverRadius: 6,
    },
  },
  scales: {
    x: {
      display: false,
      grid: {
        color: 'transparent',
        borderColor: 'transparent',
      },
    },
    y: {
      grid: {
        borderDash: [5, 5],
        color: '#37485D',
        borderColor: 'transparent',
      },
      ticks: {
        color: '#9CADC4',
        maxTicksLimit: 5,
      },
      beginAtZero: true,
      suggestedMin: 0,
    },
  },
  maintainAspectRatio: false,
  responsive: true,
}));

// No qualifying events in a match (e.g. no counterstrafing shots) is a 0% rate. Other stats keep
// the gap: a missing time-to-kill plotted as 0 would look like the best match.
function chartValue(match) {
  const { title, data } = selectedStat.value;
  return match[title] ?? (data.unit_type === 'percentage' ? 0 : null);
}

// Chart data
const chartData = computed(() => {
  if (
    props.userStatsHistory.error
    || props.userStatsHistory.length === 0
    || !selectedStat.value
  ) {
    return null;
  }

  const averageValue = formatData(
    selectedStat.value.data.rating_range_average_value,
    selectedStat.value.data.unit_type,
  );

  const reversedLabels = [...props.userStatsHistory]
    .reverse()
    .map((match) => (match.played_on || match.date).split('T')[0]);
  const reversedData = [...props.userStatsHistory]
    .reverse()
    .map((match) => formatData(chartValue(match), selectedStat.value.data.unit_type));

  return {
    labels: reversedLabels,
    datasets: [
      {
        data: reversedData,
        fill: true,
        backgroundColor: '#6B70D115',
        borderColor: '#6B70D1',
        tension: 0,
        pointRadius: 3,
        pointHoverBackgroundColor: '#6B70D1',
        pointHoverBorderColor: '#FFFFFF',
      },
      {
        data: Array(props.userStatsHistory.length).fill(averageValue),
        fill: false,
        borderColor: '#C44CB6',
        borderDash: [5, 5],
        pointRadius: 0,
        borderWidth: 1,
      },
    ],
  };
});

// Bumped per fetch so a pipeline flip mid-flight can't land the losing payload:
// the two pipelines key their stats differently, and mixing them plots nothing.
let profileRequestSeq = 0;

// Add data fetching
function getRefragCoachData() {
  if (props.user.access_level > 0) {
    profileRequestSeq += 1;
    const seq = profileRequestSeq;
    isLoading.value = true;
    // Drop the previous answer up front so a failed refetch can't leave stale
    // weaknesses or a sticky error on screen.
    userStats.value = [];
    selectedStat.value = undefined;
    userStatsError.value = null;
    matchCount.value = null;
    fetchCoachProfile(props.user)
      .then(({ stats, mostPlayedMaps: maps, matchCount: count }) => {
        if (seq !== profileRequestSeq) return;

        allStats.value = stats;
        matchCount.value = count;
        userStats.value = findWorstDeviationsAway(stats);
        mostPlayedMaps.value = maps;
        setRecommendedMap();
        setRecommendedMod();
        selectedStat.value = userStats.value[0];
        updateSelectedStatMod();
      })
      .catch((error) => {
        if (seq !== profileRequestSeq) return;

        userStatsError.value = error.response;
      })
      .finally(() => {
        if (seq !== profileRequestSeq) return;

        isLoading.value = false;
      });
  } else {
    // For users without access, set loading to false immediately
    isLoading.value = false;
  }
}

function setRecommendedMod() {
  recommendedMod.value = getMod(recommendedStat.value?.data?.recommended_mods);
}

function setRecommendedMap() {
  if (!mostPlayedMaps.value || mostPlayedMaps.value.length === 0) {
    return;
  }

  for (const mapName of mostPlayedMaps.value) {
    const csMap = store.state.modInfo?.mods[0]?.cs_maps.find((m) => m.name === mapName);
    if (csMap) {
      recommendedMap.value = csMap;
      return;
    }
  }
}

// Watch for modInfo changes
watch(
  () => store.getters.modInfo,
  () => {
    if (recommendedMap.value) return;

    setRecommendedMap();
    setRecommendedMod();
  },
);

// Watch for loading state changes and emit to parent
watch(
  () => isLoading.value,
  (newLoading) => {
    emit('loadingStateChanged', newLoading);
  },
  { immediate: true },
);

// The parent refreshes this prop when the live /me disagrees with the cached one:
// access_level decides whether we fetch at all, and a cached `0` would otherwise
// leave a paying user on the upsell until they reloaded.
watch(() => props.user?.access_level, () => {
  getRefragCoachData();
});

// Initialize data on mount
onMounted(() => {
  getRefragCoachData();
});

onUnmounted(() => {
  Chart.unregister(clickablePointsPlugin);
});
</script>

<style scoped>
.sliding-border {
  position: absolute;
  bottom: 0;
  height: 2px;
  width: 33.33%;
  background-color: theme("colors.primary.500");
  transition: left 0.3s ease-in-out;
}
.button-bg {
  border-radius: var(--Radius-radius-circle, 400px);
  border: 1px solid #3a3b5e;
  background: linear-gradient(
    180deg,
    rgba(78, 84, 200, 0.3) 0%,
    rgba(38, 41, 98, 0.3) 100%
  );
}
.topography-bg {
  background-image: url("../assets/topography-two.svg");
  background-repeat: repeat;
  background-size: 500px 500px;
}
</style>
